/**
 * @module app.core
 */

import { autoinject } from 'aurelia-framework';
import { ComponentBase, Sorting, SortDirection, SortEntry } from 'app/shared/lib/essette-site';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { IPopup } from 'app/shared/elements/popup-base';
import { apiCall } from 'app/shared/lib/essette-api';
import { IUserSearchFilters, UserService } from 'app/core/services/user-service';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { Toaster } from 'app/shared/lib/toaster';
import * as Core from 'app/core/models/essette-api';




@autoinject()
export class UserSearch extends ComponentBase {
    ////****************** Fields and Properties ************************************////
    protected userSearchFilters: IUserSearchFilters | null = null;
    protected tempUserSearchFilters: IUserSearchFilters | null = null;
    protected userResults: ReadonlyArray<Core.User> = [];
    private sorting: Sorting = new Sorting();
    protected defaultSortEntry?: SortEntry;
    protected resultCount: number = 0;
    protected pageSize: number = 25;

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;
    protected $modal!: IPopup;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: UserService,
        private readonly toaster: Toaster,
        ...rest: any[]) {

        super(...rest);
    }

    protected activate() {
        this.clearResults();
    }

    ////****************** Functions ************************************************////
    protected formSuccess(): void {
        if(!!this.$modal) {
            this.$modal.close();
            this.searchUsers();
        }
    }

    /** Handles the grid's sort event */
    protected async applySort(event: SortChangeEvent): Promise<void> {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        await this.searchUsers();
    }

    protected clearResults() {
        this.userResults = [];
        if(this.$pager) this.$pager.updatePage(0);
        this.resultCount = 0;
    }

    protected submitUserSearch() {
        this.userSearchFilters = { ...this.tempUserSearchFilters };
        this.searchUsers();
    }

    protected formatName(user: Core.User): string {
        if(user.name?.first && user.name?.last) { return user.name.last + ', ' + user.name?.first }
        else if(!user.name?.first && user.name?.last) { return user.name?.last }
        else if(user.name?.first && !user.name?.last) { return user.name?.first }
        else { return '' }
    }

    ////****************** Service Calls ********************************************////

    @apiCall
    protected async searchUsers(): Promise<void> {
        if(!this.userSearchFilters) { return; }

        // use default sort if no other sort specified
        let sort = new Sorting();
        if(this.sorting.entries.length === 0 && this.defaultSortEntry) sort.entries.push(this.defaultSortEntry);
        else sort = this.sorting;

        const options: Partial<IQueryLoadOptions> = {
            includeInactive: false,
            top: this.pageSize,
            page: this.$pager?.page || 0,
            sorting: sort
        };

        const includes: string[] = [
            'filters',
            'locations',
            'login',
            'roles'
        ];

        const fields: string[] = [
        ];

        try {
            this.state.beginLoad();
            this.userSearchFilters.hasNoExternalSystem = true; // have to filter out any user with an ExternalSystemID
            this.userSearchFilters.notInternal = true; // must filter out internal users as well since they cannot be updated
            const response = await this.service.search(this.userSearchFilters, options, includes, fields);
            this.userResults = response.results;
            this.resultCount = response.count || 0;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async deactivateUser(user: Core.User): Promise<void> {
        if(!user.id) return;

        // Show confirmation dialog only if deactivating
        if(user.isActive) {
            const result = await confirmation.showDeactivate('core:user.$', 1, false);
            if(!(result.dialogResponse === confirmation.options.OK)) return;
        }

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.service.activateUser(user.id, !user.isActive);
            isSuccessful = response.isSuccessful;

            if(isSuccessful) {
                this.submitUserSearch();
            }
        }
        catch(error) {
            throw error;
        }
        finally {
            this.toaster.standard.activate('core:user.$', !user.isActive, isSuccessful);
            this.state.endSave();
        }
    }
}

